import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Leaf, Users, Award, Wrench } from "lucide-react";
import { TEAM, TIMELINE, IMAGES, BUSINESS } from "@/data/content";
import useSEO from "@/hooks/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const VALUES = [
  { icon: Wrench, h: "Craft over speed", p: "Straight conduit, labeled panels, tidy junction boxes — the details you only see when you open the wall in ten years." },
  { icon: ShieldCheck, h: "Code, always", p: "Every job pulled with the right permit. Every install inspected. Nothing gray-market, ever." },
  { icon: Users, h: "One team, one standard", p: "Our journeymen train our apprentices. No sub-contracted variability, no rotating faces on your job." },
  { icon: Leaf, h: "Built for the grid ahead", p: "EVs, heat pumps, batteries — we install for what your home will need in 2035, not just 2025." },
];

export default function AboutUs() {
  useSEO({
    title: "About ElectroMax Systems | BC-Licensed Electricians in Metro Vancouver",
    description: "A dozen years of BC-licensed electrical work across the Lower Mainland. Owner-operated, journeyman-led, permit-first.",
  });
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden grain">
        <div className="pointer-events-none absolute -bottom-40 -left-40 w-[640px] h-[640px] bg-[#E60000]/10 blur-[140px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 em-rise">
            <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">— About ElectroMax</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter font-black">
              A dozen years <br /><span className="text-white/40">of doing the job right.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
              ElectroMax started with one Red Seal journeyman, a used Ford van, and a stubborn refusal to cut corners. Today we&rsquo;re a team of twelve doing residential and commercial electrical work across Metro Vancouver — still stubborn, still owner-operated.
            </p>
          </div>
          <div className="lg:col-span-5 em-rise" style={{ animationDelay: "120ms" }}>
            <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626] text-center">
              <Stat n="1,200+" l="Jobs Delivered" />
              <Stat n="12" l="Team Members" />
              <Stat n="5.0★" l="Google Rating" />
            </div>
          </div>
        </div>
      </section>

      {/* Story image + intro */}
      <section className="py-16 border-t border-[#262626]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— Our Story</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
              Not built for volume. <span className="text-white/40">Built for the next callback that never comes.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Daniel started ElectroMax after too many years watching sub-standard work get billed as premium. The pitch was simple: pull permits, do it once, do it right, treat customers like they&rsquo;ll refer you.
            </p>
            <p className="mt-4 text-white/70 leading-relaxed">
              Turns out that was enough. Twelve years in, we&rsquo;ve never had to advertise. Referrals fill the calendar and the crew grows one apprentice at a time.
            </p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative border border-[#262626]">
              <img src={IMAGES.about} alt="ElectroMax journeyman electrician working on a service panel in Metro Vancouver" loading="eager" fetchPriority="high" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/60">Delta, BC · Est. 2013</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— What We Stand For</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95] max-w-3xl">
            Four principles. <span className="text-black/40">No exceptions.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
            {VALUES.map((v) => (
              <div key={v.h} className="bg-white p-6 md:p-8">
                <div className="w-10 h-10 border border-[#E4E4E7] flex items-center justify-center">
                  <v.icon className="w-4 h-4 text-[#E60000]" strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-display text-xl md:text-2xl font-bold text-black leading-tight">{v.h}</h3>
                <p className="mt-3 text-sm text-black/65 leading-relaxed">{v.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 lg:py-32 border-t border-[#262626]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— Milestones</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
                A dozen years, <span className="text-white/40">in five moments.</span>
              </h2>
            </div>
          </div>
          <ol className="relative border-l border-[#262626] ml-4">
            {TIMELINE.map((t) => (
              <li key={t.year} className="pl-8 md:pl-12 pb-12 last:pb-0 relative">
                <span className="absolute left-0 -translate-x-1/2 top-1 w-3 h-3 bg-[#E60000]" />
                <div className="font-display text-3xl md:text-4xl font-black text-[#E60000]/80">{t.year}</div>
                <div className="mt-1 font-display text-xl md:text-2xl font-bold">{t.h}</div>
                <p className="mt-2 text-white/70 max-w-2xl leading-relaxed">{t.p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— The Crew</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
                Real journeymen. <span className="text-black/40">Real names.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-black/65 leading-relaxed">
              No sub-contracted labor, no rotating faces. The person quoting your job is the person doing it.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {TEAM.map((m) => (
              <div key={m.name} className="border border-[#E4E4E7] bg-white overflow-hidden group">
                <div className="aspect-[4/5] overflow-hidden bg-[#F4F4F5]">
                  <img src={m.img} alt={`${m.name} — ${m.role || "electrician"} at ElectroMax Systems`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="font-display text-lg md:text-xl font-bold text-black">{m.name}</div>
                  <div className="text-xs text-black/55 mt-1 tracking-wide">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 border-t border-[#262626]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— Paperwork</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-bold leading-[0.95]">
                Certified. Bonded. <span className="text-white/40">Insured.</span>
              </h2>
              <p className="mt-4 text-white/70 max-w-md leading-relaxed">
                Because &ldquo;trust me&rdquo; isn&rsquo;t good enough when it&rsquo;s your family in the house.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
              {[
                { icon: ShieldCheck, h: "TSBC Licensed", p: "License #LIC-00215328" },
                { icon: Leaf, h: "CleanBC Approved", p: "Registered EV installer" },
                { icon: Award, h: "$5M Liability", p: "Fully insured on every job" },
                { icon: Users, h: "WorkSafeBC", p: "Registered and compliant" },
              ].map((c) => (
                <div key={c.h} className="bg-[#0A0A0A] p-6">
                  <c.icon className="w-5 h-5 text-[#E60000]" strokeWidth={2} />
                  <div className="mt-4 font-display font-bold text-lg">{c.h}</div>
                  <div className="text-xs text-white/55 mt-1">{c.p}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-[#262626] bg-[#E60000] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none">
          <div className="w-full h-full" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 2px, transparent 2px 14px)" }} />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter font-black leading-[0.95]">
              Got a job on the horizon?
            </h2>
            <p className="mt-3 text-white/90 max-w-xl">
              Free, no-obligation estimates within one business hour. Just tell us what you need.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex gap-3">
            <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white hover:bg-black font-bold px-6 py-4 text-sm">
              Get a Free Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="bg-[#0A0A0A] py-6">
      <div className="font-display text-3xl md:text-4xl font-black">{n}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 mt-1">{l}</div>
    </div>
  );
}
